import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { COVER_EXPORT_PX, coverDocStoreWarning } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { releaseThemeMessages } from '../../music24/promote/themes/releaseThemeMessages';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
import { InspectorRow } from './controls/InspectorRow';
import { Slider } from './controls/Slider';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The inspector with nothing selected (design cover-editor d5): the cover itself. The size, the
 * release theme of the defaults, the colours of the picture, the place and the zoom of a picture
 * background, and the counts of the text boxes and the effects.
 */
const CoverInspectorComponent: React.FC = () => {
  const intl = useIntl();
  const { state, dispatch, data, readOnly } = useCoverEditor();
  const { doc } = state;
  const { background } = doc;
  const setPicture = useCallback(
    (change: { focusX?: number; focusY?: number; zoom?: number }, field: string) => {
      if (background.kind !== 'picture') {
        return;
      }
      dispatch({ type: 'setBackground', background: { ...background, ...change }, coalesce: `background:${field}` });
    },
    [background, dispatch]
  );
  const colours = useMemo(() => [...new Set(doc.palette)], [doc.palette]);
  const hasStoreWarning = useMemo(
    () => coverDocStoreWarning(doc, { title: data.song.title, artist: data.song.artistName }),
    [data.song.artistName, data.song.title, doc]
  );

  return (
    <>
      <h4 className={styles.inspectorHead}>{intl.formatMessage(coverEditorMessages.inspectorCover)}</h4>
      <div className={styles.inspectorGrid}>
        <InspectorRow label={intl.formatMessage(coverEditorMessages.coverSize)}>
          <span className={styles.fieldMono}>
            {intl.formatMessage(coverEditorMessages.coverSizeValue, { size: COVER_EXPORT_PX })}
          </span>
        </InspectorRow>
        <InspectorRow label={intl.formatMessage(coverEditorMessages.coverTheme)}>
          <span className={styles.fieldMono}>{intl.formatMessage(releaseThemeMessages[doc.themeId].name)}</span>
        </InspectorRow>
        <InspectorRow label={intl.formatMessage(coverEditorMessages.coverColours)}>
          <span className={styles.swatches}>
            {colours.map((colour) => (
              <span key={colour} className={styles.swatch} style={{ background: colour }} title={colour} />
            ))}
          </span>
        </InspectorRow>
        {background.kind === 'picture' && (
          <>
            <InspectorRow label={intl.formatMessage(coverEditorMessages.pictureZoom)}>
              <Slider
                label={intl.formatMessage(coverEditorMessages.pictureZoom)}
                value={Math.round(background.zoom * 100)}
                min={100}
                max={300}
                display={`${Math.round(background.zoom * 100)}%`}
                disabled={readOnly}
                onChange={(value) => setPicture({ zoom: value / 100 }, 'zoom')}
              />
            </InspectorRow>
            <InspectorRow label={intl.formatMessage(coverEditorMessages.pictureFocusX)}>
              <Slider
                label={intl.formatMessage(coverEditorMessages.pictureFocusX)}
                value={Math.round(background.focusX * 100)}
                min={0}
                max={100}
                display={String(Math.round(background.focusX * 100))}
                disabled={readOnly}
                onChange={(value) => setPicture({ focusX: value / 100 }, 'focusX')}
              />
            </InspectorRow>
            <InspectorRow label={intl.formatMessage(coverEditorMessages.pictureFocusY)}>
              <Slider
                label={intl.formatMessage(coverEditorMessages.pictureFocusY)}
                value={Math.round(background.focusY * 100)}
                min={0}
                max={100}
                display={String(Math.round(background.focusY * 100))}
                disabled={readOnly}
                onChange={(value) => setPicture({ focusY: value / 100 }, 'focusY')}
              />
            </InspectorRow>
          </>
        )}
        <InspectorRow label={intl.formatMessage(coverEditorMessages.coverLayers)}>
          <span className={styles.fieldMono}>
            {intl.formatMessage(coverEditorMessages.coverLayersValue, {
              texts: doc.texts.length,
              effects: doc.effects.length,
            })}
          </span>
        </InspectorRow>
      </div>
      {hasStoreWarning && (
        <p className={styles.storeNote} role="note">
          {intl.formatMessage(coverEditorMessages.storeNote)}
        </p>
      )}
      <span className={styles.grow} />
      <span className={styles.hint}>{intl.formatMessage(coverEditorMessages.coverHint)}</span>
    </>
  );
};

export const CoverInspector = TypedMemo(CoverInspectorComponent);
